export class PopupBlockedError extends Error {}
export class PopupClosedError extends Error {}

/**
 * Opens a popup on `url`
 *
 * @param {Object} options
 * @param {string} options.url The url to open in the popup
 * @param {string} [options.target='_blank'] The name of the popup window
 * @returns {Window} The popup window
 */
export const openPopup = ({ url, target = '_blank', features } = {}) => {
  const popup = window.open(url, target, features);

  if (!popup) throw new PopupBlockedError();

  window.postMessage({ openPopup: true, url, target });

  return popup;
};

/**
 * Waits for the provided popup to redirect to /redirect-callback and returns
 * the href of the redirect. Useful for implementing OAuth flows without navigating
 * out of the current session.
 * @param {Window} popup The popup window
 * @param {string} target The popup target, used to reject if a new popup is created on the same target
 * @returns {Promise<string>} The href of the popup upon completion
 */
export const waitForRedirectCallback = (popup, target) => {
  return new Promise((resolve, reject) => {
    // Interval to check if the window has been closed.
    const interval = setInterval(() => {
      if (popup.closed) {
        reject(new PopupClosedError());
        clearListeners();
      }
    }, 1000);

    // Listener for the callback from the child window
    const listener = ({ data, source }) => {
      if (popup !== source || !data.redirectCallback) return;
      resolve(data.href);
      clearListeners();
    };

    // Listener for popups which override the current popup.
    const overridePopupListener = ({ data, source }) => {
      if (source === window && data.openPopup && data.target && data.target === target) {
        reject(new PopupClosedError());
        clearListeners();
      }
    };

    window.addEventListener('message', listener);

    // Set this on the event-loop, so we dont discover our own popup
    window.addEventListener('message', overridePopupListener);

    const clearListeners = () => {
      window.removeEventListener('message', listener);
      window.removeEventListener('message', overridePopupListener);
      clearInterval(interval);
    };
  });
};
